import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CartProvider } from "@/hooks/useCart";
import { AuthProvider } from "@/hooks/useAuth";
import { LanguageProvider } from "@/hooks/useLanguage";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import NewsletterSection from "@/components/NewsletterSection";
import AdminBar from "@/components/AdminBar";
// SideCart enthält framer-motion und ist in jedem Layout. Lazy laden, damit
// framer-motion (~129 KB) NICHT im Critical Path ist – SideCart wird erst
// sichtbar, wenn der Nutzer den Warenkorb öffnet.
const SideCart = lazy(() => import("@/components/SideCart"));
import ScrollToTop from "@/components/ScrollToTop";
import ChatWidgetLauncher from "@/components/chat/ChatWidgetLauncher";
import { AnalyticsProvider } from "@/components/AnalyticsProvider";
import RecoveryHashRedirect from "@/components/RecoveryHashRedirect";
import { registerPushServiceWorker } from "@/lib/pushNotifications";
import { cn } from "@/lib/utils";
import { detectStorefront } from "@/hooks/useStorefront";

// Eager: kritischer Pfad (Startseite, Produkt, Checkout)
import HomePage from "@/pages/HomePage";
import ProductPage from "@/pages/ProductPage";
import CheckoutPage from "@/pages/CheckoutPage";
import SlugResolver from "@/pages/SlugResolver";
import NotFound from "./pages/NotFound.tsx";

// Lazy: selten besuchte Public-Pages
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const ImpressumPage = lazy(() => import("@/pages/legal/ImpressumPage"));
const AGBPage = lazy(() => import("@/pages/legal/AGBPage"));
const DatenschutzPage = lazy(() => import("@/pages/legal/DatenschutzPage"));
const OrderSuccessPage = lazy(() => import("@/pages/OrderSuccessPage"));
const SitemapPage = lazy(() => import("@/pages/SitemapPage"));
const BigPunkteInfoPage = lazy(() => import("@/pages/BigPunkteInfoPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));

// Lazy: Auth-Pages
const LoginPage = lazy(() => import("@/pages/auth/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/auth/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/auth/ResetPasswordPage"));
const AccountPage = lazy(() => import("@/pages/auth/AccountPage"));
const OrderDetailPage = lazy(() => import("@/pages/auth/OrderDetailPage"));
const EmailConfirmPage = lazy(() => import("@/pages/auth/EmailConfirmPage"));

// Lazy: Admin (gesamter Admin-Bereich aus dem Initial-Bundle entfernt)
const AdminLoginPage = lazy(() => import("@/pages/admin/AdminLoginPage"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("@/pages/admin/AdminProducts"));
const AdminCategories = lazy(() => import("@/pages/admin/AdminCategories"));
const AdminOrders = lazy(() => import("@/pages/admin/AdminOrders"));
const AdminSMMOrders = lazy(() => import("@/pages/admin/AdminSMMOrders"));
const AdminSMMPanels = lazy(() => import("@/pages/admin/AdminSMMPanels"));
const AdminAnalytics = lazy(() => import("@/pages/admin/AdminAnalytics"));
const AdminDiscountCodes = lazy(() => import("@/pages/admin/AdminDiscountCodes"));
const AdminVAT = lazy(() => import("@/pages/admin/AdminVAT"));
const AdminReviews = lazy(() => import("@/pages/admin/AdminReviews"));
const AdminBlog = lazy(() => import("@/pages/admin/AdminBlog"));
const AdminBlogEdit = lazy(() => import("@/pages/admin/AdminBlogEdit"));
const AdminProductEdit = lazy(() => import("@/pages/admin/AdminProductEdit"));
const AdminChat = lazy(() => import("@/pages/admin/AdminChat"));
const AdminTriage = lazy(() => import("@/pages/admin/AdminTriage"));
const AdminSEO = lazy(() => import("@/pages/admin/AdminSEO"));
const AdminSeoOverrides = lazy(() => import("@/pages/admin/AdminSeoOverrides"));
const AdminTranslations = lazy(() => import("@/pages/admin/AdminTranslations"));
const AdminOmnisend = lazy(() => import("@/pages/admin/AdminOmnisend"));
const AdminEmails = lazy(() => import("@/pages/admin/AdminEmails"));
const EmailCenterLayout = lazy(() => import("@/pages/admin/email/EmailCenterLayout"));
const EmailCenterOverview = lazy(() => import("@/pages/admin/email/EmailCenterOverview"));
const EmailCenterLog = lazy(() => import("@/pages/admin/email/EmailCenterLog"));
const EmailCenterAutomations = lazy(() => import("@/pages/admin/email/EmailCenterAutomations"));
const EmailCenterAutomationDetail = lazy(() => import("@/pages/admin/email/EmailCenterAutomationDetail"));
const EmailCenterLoyaltyDetail = lazy(() => import("@/pages/admin/email/EmailCenterLoyaltyDetail"));
const EmailCenterCampaigns = lazy(() => import("@/pages/admin/email/EmailCenterCampaigns"));
const EmailCenterCampaignEdit = lazy(() => import("@/pages/admin/email/EmailCenterCampaignEdit"));
const EmailCenterCampaignStats = lazy(() => import("@/pages/admin/email/EmailCenterCampaignStats"));
const EmailCenterContactsImport = lazy(() => import("@/pages/admin/email/EmailCenterContactsImport"));
const EmailCenterContacts = lazy(() => import("@/pages/admin/email/EmailCenterContacts"));
const EmailCenterSettings = lazy(() => import("@/pages/admin/email/EmailCenterSettings"));
const AdminExchangeRates = lazy(() => import("@/pages/admin/AdminExchangeRates"));
const AdminCheckoutBumps = lazy(() => import("@/pages/admin/AdminCheckoutBumps"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminCustomersPage = lazy(() => import("@/pages/admin/AdminCustomersPage"));
const AdminFinance = lazy(() => import("@/pages/admin/AdminFinance"));
const AdminLoyalty = lazy(() => import("@/pages/admin/AdminLoyalty"));
const AdminAutomations = lazy(() => import("@/pages/admin/AdminAutomations"));
const AdminMigrateImages = lazy(() => import("@/pages/admin/AdminMigrateImages"));
const AdminStatus = lazy(() => import("@/pages/admin/AdminStatus"));
const StatusPage = lazy(() => import("@/pages/StatusPage"));
const ReviewSubmitPage = lazy(() => import("@/pages/ReviewSubmitPage"));
const NewsletterUnsubscribePage = lazy(() => import("@/pages/NewsletterUnsubscribePage"));

const queryClient = new QueryClient();

// Cleanup: unregister any leftover service worker from the old Omnisend push
// integration. Runs once per device (flag stored in localStorage), after the
// page has fully loaded, so it never blocks the critical render path.
registerPushServiceWorker();

const ShopLayout = ({ children }: { children: React.ReactNode }) => (
  <div className="flex min-h-screen flex-col">
    <AdminBar />
    <Header />
    <Suspense fallback={null}>
      <SideCart />
    </Suspense>
    <main className="flex-1">{children}</main>
    <NewsletterSection />
    <Footer />
    <ChatWidgetLauncher />
  </div>
);

/** Minimaler Fallback für lazy-geladene Routes – verhindert Layout-Shift. */
const RouteFallback = () => {
  // Auf Admin-Routes den gespeicherten Dark-Mode respektieren, damit die
  // Ladeanzeige nicht plötzlich weiß aufblitzt.
  let isAdminDark = false;
  if (typeof window !== 'undefined') {
    try {
      isAdminDark =
        window.location.pathname.startsWith('/admin') &&
        localStorage.getItem('bf_admin_theme') === 'dark';
    } catch {}
  }
  return (
    <div className={cn('min-h-screen bg-background text-foreground', isAdminDark && 'dark')}>
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    </div>
  );
};


/** All shop routes (shared between default and locale-prefixed) */
function ShopRoutes() {
  return (
    <>
      <Route index element={<ShopLayout><HomePage /></ShopLayout>} />
      {/* DE product route */}
      <Route path="produkt/:slug" element={<ShopLayout><ProductPage /></ShopLayout>} />
      {/* Translated product routes */}
      <Route path="product/:slug" element={<ShopLayout><ProductPage /></ShopLayout>} />
      <Route path="prodotto/:slug" element={<ShopLayout><ProductPage /></ShopLayout>} />
      <Route path="produit/:slug" element={<ShopLayout><ProductPage /></ShopLayout>} />

      <Route path="blog" element={<ShopLayout><BlogPage /></ShopLayout>} />
      {/* Blog categories are resolved via SlugResolver at root level (no /blog/ prefix) */}
      <Route path="checkout" element={<CheckoutPage />} />
      <Route path="bestellung-erfolgreich" element={<OrderSuccessPage />} />
      <Route path="order-success" element={<OrderSuccessPage />} />

      {/* Legal */}
      <Route path="impressum" element={<ShopLayout><ImpressumPage /></ShopLayout>} />
      <Route path="imprint" element={<ShopLayout><ImpressumPage /></ShopLayout>} />
      <Route path="mentions-legales" element={<ShopLayout><ImpressumPage /></ShopLayout>} />
      <Route path="agb" element={<ShopLayout><AGBPage /></ShopLayout>} />
      <Route path="terms" element={<ShopLayout><AGBPage /></ShopLayout>} />
      <Route path="termini" element={<ShopLayout><AGBPage /></ShopLayout>} />
      <Route path="conditions" element={<ShopLayout><AGBPage /></ShopLayout>} />
      <Route path="datenschutz" element={<ShopLayout><DatenschutzPage /></ShopLayout>} />
      <Route path="privacy" element={<ShopLayout><DatenschutzPage /></ShopLayout>} />
      <Route path="confidentialite" element={<ShopLayout><DatenschutzPage /></ShopLayout>} />
      <Route path="sitemap" element={<ShopLayout><SitemapPage /></ShopLayout>} />

      {/* Contact */}
      <Route path="kontakt" element={<ShopLayout><ContactPage /></ShopLayout>} />
      <Route path="contact" element={<ShopLayout><ContactPage /></ShopLayout>} />
      <Route path="contatto" element={<ShopLayout><ContactPage /></ShopLayout>} />

      {/* Service Status */}
      <Route path="status" element={<ShopLayout><StatusPage /></ShopLayout>} />

      {/* Review submission (one-click via email) */}
      <Route path="bewertung/abgeben" element={<ShopLayout><ReviewSubmitPage /></ShopLayout>} />
      <Route path="review/submit" element={<ShopLayout><ReviewSubmitPage /></ShopLayout>} />

      {/* Newsletter Abmeldung */}
      <Route path="newsletter/abmelden" element={<ShopLayout><NewsletterUnsubscribePage /></ShopLayout>} />
      <Route path="newsletter/unsubscribe" element={<ShopLayout><NewsletterUnsubscribePage /></ShopLayout>} />
      <Route path="newsletter/disiscrivi" element={<ShopLayout><NewsletterUnsubscribePage /></ShopLayout>} />
      <Route path="newsletter/desabonnement" element={<ShopLayout><NewsletterUnsubscribePage /></ShopLayout>} />

      {/* Auth – DE */}
      <Route path="anmelden" element={<ShopLayout><LoginPage /></ShopLayout>} />
      <Route path="registrieren" element={<ShopLayout><RegisterPage /></ShopLayout>} />
      <Route path="passwort-vergessen" element={<ShopLayout><ForgotPasswordPage /></ShopLayout>} />
      <Route path="passwort-zuruecksetzen" element={<ShopLayout><ResetPasswordPage /></ShopLayout>} />
      <Route path="email-bestaetigung" element={<ShopLayout><EmailConfirmPage /></ShopLayout>} />
      <Route path="konto" element={<ShopLayout><AccountPage /></ShopLayout>} />
      <Route path="konto/bestellung/:orderId" element={<ShopLayout><OrderDetailPage /></ShopLayout>} />
      {/* Auth – translated */}
      <Route path="login" element={<ShopLayout><LoginPage /></ShopLayout>} />
      <Route path="connexion" element={<ShopLayout><LoginPage /></ShopLayout>} />
      <Route path="accesso" element={<ShopLayout><LoginPage /></ShopLayout>} />
      <Route path="register" element={<ShopLayout><RegisterPage /></ShopLayout>} />
      <Route path="registrazione" element={<ShopLayout><RegisterPage /></ShopLayout>} />
      <Route path="inscription" element={<ShopLayout><RegisterPage /></ShopLayout>} />
      <Route path="account" element={<ShopLayout><AccountPage /></ShopLayout>} />
      <Route path="compte" element={<ShopLayout><AccountPage /></ShopLayout>} />
      <Route path="account/bestellung/:orderId" element={<ShopLayout><OrderDetailPage /></ShopLayout>} />
      <Route path="compte/bestellung/:orderId" element={<ShopLayout><OrderDetailPage /></ShopLayout>} />

      {/* Catch-all slug resolver for categories / blog posts */}
      <Route path=":slug" element={<ShopLayout><SlugResolver /></ShopLayout>} />
      <Route path="*" element={<ShopLayout><NotFound /></ShopLayout>} />
    </>
  );
}

const App = () => {
  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <RecoveryHashRedirect />
        <AuthProvider>
          <CartProvider>
            <LanguageProvider>
              <AnalyticsProvider>
                <Suspense fallback={<RouteFallback />}>
                <Routes>
                  {/* Admin routes */}
                  <Route path="/admin/login" element={<AdminLoginPage />} />
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="products" element={<AdminProducts />} />
                    <Route path="products/:id" element={<AdminProductEdit />} />
                    <Route path="categories" element={<AdminCategories />} />
                    <Route path="orders" element={<AdminOrders />} />
                    <Route path="smm-orders" element={<AdminSMMOrders />} />
                    <Route path="smm-panels" element={<AdminSMMPanels />} />
                    <Route path="users" element={<Navigate to="/admin/customers" replace />} />
                    <Route path="customers" element={<AdminCustomersPage />} />
                    <Route path="analytics" element={<AdminAnalytics />} />
                    <Route path="discount-codes" element={<AdminDiscountCodes />} />
                    <Route path="vat" element={<AdminVAT />} />
                    <Route path="exchange-rates" element={<AdminExchangeRates />} />
                    <Route path="reviews" element={<AdminReviews />} />
                    <Route path="reviews/emails" element={<AdminEmails />} />
                    {/* Legacy redirects → new E-Mail-Center */}
                    <Route path="emails" element={<Navigate to="/admin/email/log" replace />} />
                    <Route path="email-uebersicht" element={<Navigate to="/admin/email/log" replace />} />
                    <Route path="automations" element={<Navigate to="/admin/email/automations" replace />} />
                    {/* New E-Mail-Center */}
                    <Route path="email" element={<EmailCenterLayout />}>
                      <Route index element={<EmailCenterOverview />} />
                      <Route path="log" element={<EmailCenterLog />} />
                      <Route path="automations" element={<EmailCenterAutomations />} />
                      <Route path="automations/loyalty_thankyou" element={<EmailCenterLoyaltyDetail />} />
                      <Route path="automations/:slug" element={<EmailCenterAutomationDetail />} />
                      <Route path="campaigns" element={<EmailCenterCampaigns />} />
                      <Route path="campaigns/new" element={<EmailCenterCampaignEdit />} />
                      <Route path="campaigns/:id" element={<EmailCenterCampaignEdit />} />
                      <Route path="campaigns/:id/stats" element={<EmailCenterCampaignStats />} />
                      <Route path="settings" element={<EmailCenterSettings />} />
                      {/* Legacy → new customers/contacts location */}
                      <Route path="contacts/import" element={<Navigate to="/admin/customers/contacts/import" replace />} />
                      <Route path="contacts" element={<Navigate to="/admin/customers/contacts" replace />} />
                    </Route>
                    {/* Legacy contacts routes → tab in customers page */}
                    <Route path="customers/contacts" element={<Navigate to="/admin/customers?tab=contacts" replace />} />
                    <Route path="customers/contacts/import" element={<EmailCenterContactsImport />} />
                    <Route path="blog" element={<AdminBlog />} />
                    <Route path="blog/:id" element={<AdminBlogEdit />} />
                    <Route path="chat" element={<AdminChat />} />
                    <Route path="support" element={<AdminChat />} />
                    <Route path="triage" element={<AdminTriage />} />
                    <Route path="seo" element={<AdminSEO />} />
                    <Route path="seo-overrides" element={<AdminSeoOverrides />} />
                    <Route path="translations" element={<AdminTranslations />} />
                    <Route path="checkout-bumps" element={<AdminCheckoutBumps />} />
                    <Route path="omnisend" element={<AdminOmnisend />} />
                    <Route path="finance" element={<AdminFinance />} />
                    <Route path="loyalty" element={<AdminLoyalty />} />
                    <Route path="status" element={<AdminStatus />} />
                    <Route path="migrate-images" element={<AdminMigrateImages />} />
                  </Route>

                  {/* Big-Punkte Info-Seite – exakt ein Slug pro Sprache */}
                  <Route path="/big-punkte" element={<ShopLayout><BigPunkteInfoPage /></ShopLayout>} />
                  <Route path="/en/big-points" element={<ShopLayout><BigPunkteInfoPage /></ShopLayout>} />
                  <Route path="/it/punti-fedelta" element={<ShopLayout><BigPunkteInfoPage /></ShopLayout>} />
                  <Route path="/fr/points-fidelite" element={<ShopLayout><BigPunkteInfoPage /></ShopLayout>} />

                  {/* Default (DE) shop routes */}
                  <Route path="/">{ShopRoutes()}</Route>

                  {/* Locale-prefixed shop routes — NUR CH mountet EN/IT/FR.
                      Auf bigfollow.at fallen /en /it /fr auf die normale
                      NotFound-Route in ShopRoutes() (letzte Route path="*").
                      Google räumt bereits indexierte Übersetzungs-URLs mit
                      der Zeit selbst ab. */}
                  {detectStorefront() === 'ch' && (
                    <>
                      <Route path="/en">{ShopRoutes()}</Route>
                      <Route path="/it">{ShopRoutes()}</Route>
                      <Route path="/fr">{ShopRoutes()}</Route>
                    </>
                  )}
                </Routes>
                </Suspense>
              </AnalyticsProvider>
            </LanguageProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
